import api from './api'

const VNC_API = 'vnc'

/**
 * Test VNC file transfer connection for a modality
 * @param {string} modalityId - The modality ID
 * @returns {Promise<{success: boolean, server: string}>}
 */
export const testConnection = (modalityId) =>
  api.postResource(`${VNC_API}/modalities/${modalityId}/files/test`)

/**
 * List directory contents on remote VNC server
 * @param {string} modalityId - The modality ID
 * @param {string} path - Remote directory path (e.g., "C:/MedCom/log")
 * @returns {Promise<{path: string, files: Array<{name: string, size: number, isDirectory: boolean, modTime: string, isHidden: boolean}>}>}
 */
export const listDirectory = (modalityId, path) =>
  api.postResource(`${VNC_API}/modalities/${modalityId}/files/list`, { path })

/**
 * Download a file from remote VNC server
 * @param {string} modalityId - The modality ID
 * @param {string} path - Remote file path
 * @returns {Promise<{path: string, content: string, size: number}>} - content is base64 encoded
 */
export const downloadFile = (modalityId, path) =>
  api.postResource(`${VNC_API}/modalities/${modalityId}/files/download`, { path })

/**
 * Upload a file to remote VNC server
 * @param {string} modalityId - The modality ID
 * @param {string} path - Remote file path
 * @param {string} content - Base64 encoded file content
 * @returns {Promise<{success: boolean, path: string, bytesWritten: number}>}
 */
export const uploadFile = (modalityId, path, content) =>
  api.postResource(`${VNC_API}/modalities/${modalityId}/files/upload`, { path, content })

/**
 * Helper to decode base64 content and trigger download in browser
 * @param {string} base64Content - Base64 encoded file content
 * @param {string} filename - Filename for the download
 * @param {string} mimeType - MIME type (default: application/octet-stream)
 */
export const downloadToBrowser = (base64Content, filename, mimeType = 'application/octet-stream') => {
  const byteCharacters = atob(base64Content)
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  const blob = new Blob([byteArray], { type: mimeType })
  
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

/**
 * Helper to convert File object to base64 for upload
 * @param {File} file - File object from input
 * @returns {Promise<string>} - Base64 encoded content
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      // Remove data URL prefix (e.g., "data:application/octet-stream;base64,")
      const base64 = reader.result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = (error) => reject(error)
  })
}

